import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useSite, absoluteUrl } from "../context/SiteContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/leadership", label: "Leadership" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/clients", label: "Clients" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { settings } = useSite();
  const brand = settings.brand;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-[#1F3550] transition-colors ${
        scrolled ? "bg-[#050A0F]/95" : "bg-[#050A0F]/80"
      } backdrop-blur-md`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={absoluteUrl(brand.logoUrl)}
              alt={brand.name}
              className="h-12 w-12 object-contain"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-heading font-black text-base tracking-wider text-[#F8FAFC]">
                {brand.name}
              </span>
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12] uppercase">
                Engineering & Constructions
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
                    isActive
                      ? "text-[#F39C12]"
                      : "text-[#94A3B8] hover:text-[#F8FAFC]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#D68910] transition-colors"
          >
            Request Quote
            <ArrowRight className="h-4 w-4" />
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden text-[#F8FAFC] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#1F3550] bg-[#050A0F]"
        >
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `py-3 border-b border-[#1F3550]/60 text-sm uppercase tracking-wide ${
                    isActive ? "text-[#F39C12]" : "text-[#94A3B8]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-3 text-xs font-bold uppercase tracking-widest"
            >
              Request Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
